/**
 * History: the CRM's own timeline for this machine.
 *
 * **Not the Events tab.** That one reads the raw Windows logs live and stores nothing, so it needs
 * the device online and shows only what the machine still holds. This is the server's copy: it reads
 * while the device is asleep, survives the machine being rebuilt, and is retained after the device
 * is decommissioned. Nothing here touches the device at all, which is why it loads at page open
 * where the event log waits to be asked.
 *
 * **Paging walks backwards from an id, never by offset.** Entries keep arriving while a technician
 * reads - a sign-in, a heartbeat transition, an install finishing - and an offset would slide the
 * page under them, showing a row twice or skipping one entirely.
 */
import { insets, type Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import {
  DeviceEventSeverity,
  DeviceEventSource,
  type DeviceEventDto
} from '../../contracts.gen.js';
import { eventLabel, HISTORY_CATEGORIES } from '../../events.js';
import { localDateTime, plural } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { dot } from '../../ui/Dot.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, field, fieldRow, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/** A render cap. Past it the filters are the way to look further back, and the status line says so. */
const MAX_ROWS = 400;

/** The server's default page. */
const PAGE_SIZE = 100;

export interface HistoryTab extends DeviceTab {
  /** A `DeviceHistoryChanged` push for this device. */
  refresh(): void;
}

export function historyTab(dc: DeviceTabContext): HistoryTab {
  let entries: DeviceEventDto[] = [];
  let hasMore = false;
  let busy = false;
  let loaded = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();

  const categoryPicker = comboBox(HISTORY_CATEGORIES, {
    itemText: c => c.label,
    selectedIndex: 0,
    width: 180,
    autoWidth: false,
    // The combo re-queries on its own, unlike the search box: picking a family is a complete
    // instruction, while half-typed text is not.
    onSelectionChanged: () => void load()
  });
  const searchBox = textBox({ hAlign: 'stretch' });

  const searchButton = button('Search', () => void load(), true);
  const resetButton = button('Reset', () => void reset());
  const refreshButton = button('Refresh', () => void load());

  const filters = fieldRow(
    field('Show', categoryPicker),
    field('Search the entries (a user, an application, an action)', searchBox)
  );
  const actions = stackOf(buttonRow(), searchButton, resetButton, refreshButton);

  const empty = muted(
    'Nothing recorded for this device yet. Sign-ins, software changes, and everything staff do here ' +
      'are logged as they happen.'
  );

  const rows = new CardList<DeviceEventDto>({
    key: e => e.id,
    build: e => buildRow(e),
    max: MAX_ROWS,
    spacing: 0
  });
  const scroller = new BoundedScroller(520);
  scroller.content.controls.add(rows.panel);

  const moreButton = button('Load older entries', () => void loadMore());
  moreButton.visible = false;

  const body = card();
  body.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      filters,
      actions,
      status.label,
      empty,
      scroller,
      moreButton,
      text(
        'Kept by the CRM, not the machine: sign-ins, sign-outs, locks and reboots come from the agent ' +
          "(with the device's own timestamps, so a machine that was offline reports them where they " +
          'happened); software installs and removals are detected by comparing inventories; everything ' +
          'staff do from here is recorded with who did it. Readable while the device is offline, and ' +
          'retained after it is decommissioned.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );

  panel.controls.addRange([h2('History'), body]);

  function buildRow(entry: DeviceEventDto): Control {
    // Severity colours the dot only. The timeline has to stay readable in bulk, and a warning - a
    // device going offline, an unexpected shutdown, an archive - still catches the eye when scanning.
    // 8px and top-aligned, like the XAML's Ellipse: this is a severity marker in a dense timeline,
    // not a presence dot beside a heading.
    const marker = dot(false, {
      size: 8,
      vAlign: 'start',
      margin: insets(2, 6, 0, 0),
      tone:
        entry.severity === DeviceEventSeverity.Error
          ? 'danger'
          : entry.severity === DeviceEventSeverity.Warning
            ? 'warn'
            : entry.source === DeviceEventSource.Agent
              ? 'info'
              : 'muted'
    });

    const heading = inlineRow();
    heading.controls.add(chip(eventLabel(entry.kind)));
    heading.controls.add(value(entry.summary, { font: FONT.small, wrap: true }));

    const right = stackOf(
      vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false }),
      heading
    );
    if (entry.detail) right.controls.add(text(entry.detail, { role: 'muted', font: FONT.tiny, wrap: true }));
    // "Reported by the agent" is worth saying: it tells a technician the entry came off the machine
    // itself rather than from something somebody clicked in the CRM.
    const actor = entry.actor
      ? `by ${entry.actor}`
      : entry.source === DeviceEventSource.Agent
        ? 'reported by the agent'
        : '';
    if (actor) right.controls.add(text(actor, { role: 'muted', font: FONT.tiny }));

    const row = splitRow({ spacing: GAP.sm, align: 'start' });
    row.controls.add(marker);
    row.controls.add(value(localDateTime(entry.whenUtc), { font: FONT.small, role: 'muted' }));
    fillWith(row, right);
    return row;
  }

  function query(beforeId?: number): crm.DeviceHistoryQuery {
    const category = categoryPicker.selectedItem?.category ?? null;
    const search = searchBox.text.trim();
    return {
      limit: PAGE_SIZE,
      ...(category !== null ? { category } : {}),
      ...(search ? { search } : {}),
      ...(beforeId !== undefined ? { beforeId } : {})
    };
  }

  function applyRows(): void {
    rows.set(entries);
    scroller.fit();
    empty.visible = loaded && entries.length === 0 && !busy;
    // The server says whether there is more - it is not a guess from the page size - and the cap is
    // the other half: past it, paging further would render rows nobody can find.
    moreButton.visible = hasMore && entries.length < MAX_ROWS;
  }

  async function load(): Promise<void> {
    if (busy) return;
    busy = true;
    loaded = true;
    setEnabled(false);
    status.busy('Loading…');
    try {
      const page = await crm.getDeviceHistory(dc.deviceId, query(), dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      entries = page.events.slice(0, MAX_ROWS);
      hasMore = page.hasMore;
      applyRows();
      status.set(
        entries.length === 0
          ? 'Nothing recorded for these filters yet.'
          : `${plural(entries.length, 'entry', 'entries')}, newest first.`
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) {
        entries = [];
        applyRows();
        status.error(describeError(error));
      }
    } finally {
      busy = false;
      setEnabled(true);
      dc.relayout();
    }
  }

  async function loadMore(): Promise<void> {
    if (busy || entries.length === 0) return;
    busy = true;
    setEnabled(false);
    status.busy('Loading older entries…');
    try {
      const oldest = entries[entries.length - 1]!.id;
      const page = await crm.getDeviceHistory(dc.deviceId, query(oldest), dc.ctx.dispose.signal());
      if (dc.ctx.cancelled()) return;
      entries = [...entries, ...page.events].slice(0, MAX_ROWS);
      hasMore = page.hasMore;
      applyRows();
      status.set(
        `${plural(entries.length, 'entry', 'entries')}, newest first.` +
          (entries.length >= MAX_ROWS
            ? `  Showing the newest ${MAX_ROWS} — narrow the filters to look further back.`
            : '')
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    } finally {
      busy = false;
      setEnabled(true);
      dc.relayout();
    }
  }

  async function reset(): Promise<void> {
    categoryPicker.selectedIndex = 0;
    searchBox.text = '';
    await load();
  }

  function setEnabled(enabled: boolean): void {
    searchButton.enabled = enabled;
    resetButton.enabled = enabled;
    refreshButton.enabled = enabled;
    moreButton.enabled = enabled;
  }

  void load();

  return {
    panel,
    refresh() {
      // A push means one new entry at the top, so this reloads the newest page rather than paging:
      // merging a live entry into a list that may be scrolled to last week's rows would put it
      // somewhere nobody is looking, and the newest page is what the reader is on.
      if (!busy) void load();
    }
  };
}
